import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import React, { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { FieldError, FormLayout, FormRow, FormSection, Select } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { CallShiftDifferentialType } from '~/types/hlx/enums';
import { InfoMessage } from '../../../components/InfoMessage';
import { buildShiftAllocationMessage } from '../../../components/shiftAllocationMessage';
import { SHIFT_DIFF_VALUE_LABELS, ShiftTableForm } from '../../../components/ShiftTableForm';
import { useJobListingActorRef } from '../../jobListingProvider';
import { emptyCallShiftDetailsForm, SHIFT_DIFF_TYPE_OPTIONS } from './constants';
import { OnCallShiftDetailsFormSchema, OnCallShiftDetailsFormValues } from './types';
import { transformApiToOnCallShiftDetailsForm } from './utils';

const TAGS = CT.employer.jobListings.callShiftDetailsForm;

export const OnCallShiftDetailsForm = ({ footer }: { footer: React.ReactNode }) => {
  const jobListingRef = useJobListingActorRef();
  const { onCallShiftDetailsRef, listing } = useSelector(jobListingRef, (state) => state.context);
  const detail = listing?.detail;

  const methods = useCustomForm<OnCallShiftDetailsFormValues>({
    // @ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(OnCallShiftDetailsFormSchema),
    defaultValues: listing?.detail?.id
      ? transformApiToOnCallShiftDetailsForm(listing)
      : emptyCallShiftDetailsForm,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isDirty, errors },
    watch,
    setError,
    clearErrors,
  } = methods;

  useEffect(() => {
    if (listing) {
      setValue('jobListingId', listing.id);
      setValue('id', listing.detail?.id);
    }
  }, [listing]);

  // [MCV-1542] The allocation total lives at the top of a tall form while the
  // Next button sits at the bottom, and the mismatch is a form-level (root)
  // error, so react-hook-form's built-in focus-first-error has nothing to focus.
  // Without this the user is told "no" with no indication of where.
  const allocationErrorRef = useRef<HTMLDivElement>(null);
  const revealAllocationError = () => {
    allocationErrorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const onSubmitSuccess = (values: OnCallShiftDetailsFormValues) => {
    if (totalShiftsAllocated !== totalShifts) {
      setError('root.shiftAllocation', {
        type: 'custom',
        message: buildShiftAllocationMessage(totalShiftsAllocated, totalShifts),
      });
      revealAllocationError();
      return;
    }
    if (isDirty) {
      onCallShiftDetailsRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else {
      jobListingRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Shift Details Form Submit Error: ', err, methods.getValues());
  };

  const CALL_SHIFT_ALLOCATION_FIELDS: (keyof OnCallShiftDetailsFormValues)[] = [
    'numDayCallShifts',
    'numEveCallShifts',
    'numNightCallShifts',
    'numOfWeDayCallShifts',
    'numOfWeEveCallShifts',
    'numOfWeNightCallShifts',
    'numHolDayCallShifts',
    'numHolEveCallShifts',
    'numHolNightCallShifts',
  ];
  const SHIFT_ALLOCATION_FIELDS = [
    'numDayShifts',
    'numEveShifts',
    'numNightShifts',
    'numOfWeDayShifts',
    'numOfWeEveShifts',
    'numOfWeNightShifts',
    'numHolDayShifts',
    'numHolEveShifts',
    'numHolNightShifts',
  ];

  const shiftsAllocatedFormValues = watch(
    CALL_SHIFT_ALLOCATION_FIELDS as (keyof OnCallShiftDetailsFormValues)[]
  );

  const regularShiftsAllocatedFormValues = watch(
    SHIFT_ALLOCATION_FIELDS as (keyof OnCallShiftDetailsFormValues)[]
  );

  const callShiftDiffType = watch('callShiftDiffType');

  const totalShifts = detail?.numOfShifts ?? 0;

  let totalShiftsAllocated = 0;
  CALL_SHIFT_ALLOCATION_FIELDS.forEach((_field, idx) => {
    totalShiftsAllocated += Number(shiftsAllocatedFormValues[idx]) || 0;
  });
  SHIFT_ALLOCATION_FIELDS.forEach((_field, idx) => {
    totalShiftsAllocated += Number(regularShiftsAllocatedFormValues[idx]) || 0;
  });

  // [MCV-1542] A root error is not tied to a field, so nothing keeps it in sync
  // as the user corrects the counts. While the message is showing, recompute it
  // on every change: clear it once the totals agree, otherwise re-derive the
  // message so a partial correction (e.g. correcting 25-short down to 10-short)
  // never leaves the OLD count on screen. The message-equality check keeps this
  // a no-op once the text has settled: `errors.root?.shiftAllocation` is a
  // dependency and setError rewrites it, so re-setting an identical message
  // would re-run this effect on every keystroke for no reason.
  useEffect(() => {
    if (!errors.root?.shiftAllocation) return;
    if (totalShiftsAllocated === totalShifts) {
      clearErrors('root.shiftAllocation');
      return;
    }
    const message = buildShiftAllocationMessage(totalShiftsAllocated, totalShifts);
    if (errors.root.shiftAllocation.message !== message) {
      setError('root.shiftAllocation', { type: 'custom', message });
    }
  }, [totalShiftsAllocated, totalShifts, errors.root?.shiftAllocation, setError, clearErrors]);

  const CALL_SHIFT_DIFFERENTIAL_FIELDS = [
    'dayCallShiftRateDiff',
    'eveCallShiftRateDiff',
    'nightCallShiftRateDiff',
    'weDayCallShiftRateDiff',
    'weEveCallShiftRateDiff',
    'weNightCallShiftRateDiff',
    'holDayCallShiftRateDiff',
    'holEveCallShiftRateDiff',
    'holNightCallShiftRateDiff',
  ];

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <InfoMessage
            withOwnMargin={false}
            message={
              'Call differentials compensate providers for the inconvenience of being on-call during premium times. Weekend and holiday on-call typically commands 50-100% premium over weekday rates.'
            }
          />

          <h3 className="sub-heading-sb">On-Call Shift Distribution</h3>
          {/* [MCV-1542] `scroll-mt` keeps the row clear of the sticky wizard
            header when revealAllocationError() brings it into view. */}
          <div
            ref={allocationErrorRef}
            className="flex flex-wrap items-center gap-x-3 scroll-mt-24"
          >
            <p className="body-text-sb">
              Total Shifts to Allocate: {totalShiftsAllocated}/{totalShifts}
            </p>
            <ErrorMessage
              errors={errors}
              name="root.shiftAllocation"
              render={({ message }) => (
                // FieldError's default `mt-1.5` positions it *below* a field; inline
                // beside the total, that margin sits the message low against the
                // text it belongs to. className replaces (not appends to) that
                // margin slot, so mt-0 here aligns the message with the total.
                <FieldError message={message} className="mt-0" />
              )}
            />
          </div>
          <ShiftTableForm
            inputFields={CALL_SHIFT_ALLOCATION_FIELDS}
            tags={TAGS}
            withOwnMargin={false}
          />
          <div className="w-full border-t border-grey-900" />
          <h3 className="sub-heading-sb">On-Call Shift Differentials from the Weekday Rate</h3>
          <p className="body-text-sb bg-primary-t-900 rounded-lg p-4">
            Base rate: ${detail?.pricePerHour} ($/hr)
          </p>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                name="callShiftDiffType"
                description="Example Multiplier: 1.5"
                cyTag={TAGS.shiftDiffType}
                label="Differential Type"
                options={SHIFT_DIFF_TYPE_OPTIONS}
              />
            </FormRow>
          </FormSection>
          <ShiftTableForm
            inputFields={CALL_SHIFT_DIFFERENTIAL_FIELDS}
            valueLabel={
              SHIFT_DIFF_VALUE_LABELS[callShiftDiffType.value as CallShiftDifferentialType]
            }
            tags={TAGS}
            defaultVal={`${callShiftDiffType.value === 'INCR_HOURLY_RATE' ? '+$0' : 'x1.00'}`}
            withOwnMargin={false}
          />
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
